// Client-side capability model. The backend is the source of truth (every admin
// endpoint is @PreAuthorize-gated); this only decides what the UI offers so users
// don't see doors they can't open. Keyed off the JWT `role` claim.
//
// Backend capability roles (modules/auth Role enum, admin subset):
//   ADMIN_VIEWER, ADMIN_OPS, ADMIN_MARKETING, ADMIN_SUPER
// Note: the JWT does NOT carry the management tier (AdminLevel); staff/audit are
// gated on ADMIN_SUPER server-side, which we mirror here.

export type AdminRole = "ADMIN_SUPER" | "ADMIN_OPS" | "ADMIN_MARKETING" | "ADMIN_VIEWER";

export type Permission =
  | "dashboard.view"
  | "catalog.manage"
  | "orders.manage"
  | "inventory.manage"
  | "analytics.view"
  | "customers.manage"
  | "staff.manage" // Staff Registry — create/edit/suspend admins
  | "audit.view"; // Security Audit log

const ROLE_PERMISSIONS: Record<AdminRole, Permission[]> = {
  ADMIN_SUPER: [
    "dashboard.view",
    "catalog.manage",
    "orders.manage",
    "inventory.manage",
    "analytics.view",
    "customers.manage",
    "staff.manage",
    "audit.view",
  ],
  ADMIN_OPS: [
    "dashboard.view",
    "catalog.manage",
    "orders.manage",
    "inventory.manage",
    "customers.manage",
    "analytics.view",
  ],
  ADMIN_MARKETING: ["dashboard.view", "catalog.manage", "analytics.view"],
  ADMIN_VIEWER: ["dashboard.view", "analytics.view"],
};

export function isAdminRole(role: string | undefined | null): role is AdminRole {
  return (
    role === "ADMIN_SUPER" ||
    role === "ADMIN_OPS" ||
    role === "ADMIN_MARKETING" ||
    role === "ADMIN_VIEWER"
  );
}

export function permissionsFor(role: string | undefined | null): Permission[] {
  return isAdminRole(role) ? ROLE_PERMISSIONS[role] : [];
}

export function hasPermission(role: string | undefined | null, permission: Permission): boolean {
  return permissionsFor(role).includes(permission);
}

/** Human label for a capability role. */
export function roleLabel(role: string | undefined | null): string {
  switch (role) {
    case "ADMIN_SUPER":
      return "Super Admin";
    case "ADMIN_OPS":
      return "Operations";
    case "ADMIN_MARKETING":
      return "Marketing";
    case "ADMIN_VIEWER":
      return "Viewer";
    default:
      return role ?? "Admin";
  }
}

const PLATFORM_TENANT = "__PLATFORM__";

/** Human label for a tenant id, flagging the global platform tenant. */
export function tenantLabel(tenantId: string | undefined | null): string {
  if (!tenantId) return "—";
  if (tenantId === PLATFORM_TENANT) return "Platform (global)";
  return tenantId;
}

export function isPlatformTenant(tenantId: string | undefined | null): boolean {
  return tenantId === PLATFORM_TENANT;
}

/** Bootstrap super admins (JWT `adminLevel` claim — BLK-API-018) manage tenants and
 * may scope the session to any storefront tenant via X-Tenant-Override. */
export function isBootstrapLevel(adminLevel: string | undefined | null): boolean {
  return adminLevel === "BOOTSTRAP_SUPER";
}
